import {toArray} from "../ValueUtils";
import {ValidationError} from "./ValidationError";
import {Expressions} from "./Expressions";
import {Classes, Types} from "./Constants";
import {Validator} from "./Validator";

export interface ValidationResult {
    readonly isValid: boolean;
    readonly isWarning: boolean;
    readonly message: string;
}

type ValidationFailureFunc = (res: ValidationResult) => void;

export class Validators {
    static readonly expressions = Expressions; // Prefer using direct imports
    static readonly types = Object.freeze(Types); // Prefer using direct imports
    static readonly classes = Object.freeze(Classes); // Prefer using direct imports

    private static retrieveValidators(element: HTMLElement) {
        if (!element) {
            return [];
        }
        // Hybrid implementation to allow JSDOM to work in unit tests
        const s = typeof element.dataset !== "undefined"
            ? element.dataset.validators
            : element.getAttribute("data-validators");
        if (!s) {
            return [];
        }
        return s.split(",")
            .map((s) => s.trim())
            .filter((s) => !(!s));
    }

    private static setValidators(element: HTMLElement, typesArray: string[]) {
        if (!element) {
            return;
        }

        const stringValue = Array.isArray(typesArray) ? typesArray.join(",") : null;
        // Hybrid implementation to allow JSDOM to work in unit tests
        if (typeof element.dataset !== "undefined") {
            if (stringValue) {
                element.dataset.validators = stringValue;
            } else {
                delete element.dataset.validators;
            }
        } else if (stringValue) {
            element.setAttribute("data-validators", stringValue);
        } else {
            element.removeAttribute("data-validators");
        }
    }

    static hasValidator(element: HTMLElement, validatorType: string): boolean {
        if (!element || !validatorType) {
            return false;
        }
        return Validators.retrieveValidators(element).includes(validatorType);
    }

    static addValidatorAttribute(element: HTMLElement, validatorType: string) {
        if (!element || !validatorType) {
            return;
        }

        const toAdd = String(validatorType).trim();
        const validators = Validators.retrieveValidators(element);
        if (!validators.includes(toAdd)) {
            validators.push(toAdd);
        }
        Validators.setValidators(element, validators);
    }

    static removeValidatorAttribute(element: HTMLElement, validatorType: string) {
        if (!element || !validatorType) {
            return;
        }

        const toRemove = String(validatorType).trim();
        const validators = Validators.retrieveValidators(element);
        Validators.setValidators(element, validators.filter((s) => s && s !== toRemove));
    }

    static addValidators(objs: unknown) {
        const elements = toArray(objs) as HTMLElement[];
        const validators: Validator[] = [];
        elements.forEach((el) => {
            const types = Validators.retrieveValidators(el);
            if (!types || types.length === 0) {
                return;
            }
            validators.push(new Validator(el, types));
        });
        return validators;
    }

    static removeValidators(objs: unknown) {
        const elements = toArray(objs) as HTMLElement[];
        elements.forEach((el) => {
            this.setValidators(el, null);
        });
    }

    static addEventListeners(validators: Validator[]) {
        validators.forEach((validator) => {
            validator.init();
        })
    }

    static clearErrors($fromParent: JQuery): void {
        if (!$fromParent || $fromParent.length === 0) {
            return;
        }

        $fromParent
            .find("." + Classes.ERROR)
            .removeClass(Classes.ERROR);
    }

    static load(context: HTMLElement | JQuery): Validator[] {
        const validators: Validator[] = [];
        if (!context) {
            console.error('Missing context parameter for locating elements for validation. No validators loaded.');
            return validators;
        }

        const contextEl = context instanceof jQuery ? (context as JQuery)[0] : context as HTMLElement;
        contextEl.querySelectorAll("[data-validators]").forEach((el: HTMLElement) => {
            validators.push(new Validator(el, this.retrieveValidators(el)));
        });
        return validators;
    }

    // Note that this method does not completely support the "warning" validation type
    static validateNoAlert(validators: Validator[], e: Event) {
        return callValidate(validators, e);
    }

    // NOTE: If you are looking for the validate() function that displays a dialog on error,
    // see ValidatorsWithDialogs.

    /**
     * Performs validation of the provided validators, returning a promise
     * that resolves when complete.
     *
     * @param validators The validators returned from load()
     * @param $fromParent The JQuery object for the parent of the elements in the validators.
     * @returns Resolves with a boolean indicating if the result was to
     * display a warning or not. If the promise rejects then validation failed.
     */
    static async validateAsync(validators: Validator[], $fromParent: JQuery): Promise<boolean> {
        const res = performValidation(validators, $fromParent);
        if (!res.isValid) {
            throw new ValidationError(res.message);
        }
        return res.isWarning;
    }

    // Note that this method does support the "warning" validation type
    static validateOne(element: HTMLElement): boolean {
        const validators = Validators.retrieveValidators(element);
        if (!validators || validators.length === 0) {
            // No validation definition, so always "passes"
            return true;
        }
        return new Validator(element, validators).validate().isValid;
    }
}

function performValidation(validators: Validator[], $fromParent: JQuery): ValidationResult {
    if ($fromParent && $fromParent.length === 0) {
        console.error("Parent provided, but has no entries; cannot perform validation");
        // FUTURE: throw exception instead of returning failed validation?
        return {
            isValid: false,
            isWarning: false,
            message: null
        };
    }

    let result = true;
    let showWarning = false;
    let message: string = null;

    validators.forEach((validator) => {
        if (!$fromParent || $fromParent.find(validator.element).length > 0) {
            const isValidationDisabled = validator.element.getAttribute("do-validation") === "false";
            if (!isValidationDisabled) {
                const r = validator.validate();
                if (r.message && !message) {
                    message = r.message;
                }
                if (r.isValid && validator.inError()) {
                    // marked as "in error", but must be a warning as isValid is true (validation succeeded)
                    showWarning = true;
                }
                result = r.isValid && result;
            }
        }
    });
    return {
        isValid: result,
        isWarning: showWarning,
        message: message
    };
}

// Note that this method does not completely support the "warning" validation type
export function callValidate(
    validators: Validator[],
    e: Event,
    $fromParent: JQuery = null,
    onFailureFunc: ValidationFailureFunc = null,
): boolean {
    const res = performValidation(validators, $fromParent);

    if (!res.isValid) {
        if (typeof onFailureFunc === "function") {
            onFailureFunc(res);
        }
        if (e) {
            e.preventDefault?.();
            e.stopImmediatePropagation?.();
        }
    }
    return res.isValid;
}
